import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { STATEMENTS } from "@/constants/testIds";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { Download, FileText, TrendingUp, Eye } from "lucide-react";
import { toast } from "sonner";
import PdfPreviewModal from "@/components/PdfPreviewModal";
import { useSectionTheme } from "@/hooks/useSectionTheme";
import { LandlordImportCard, StatementQueueCard } from "@/components/StatementQueue";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const gbp = (n) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n || 0);

export default function Statements() {
  const sectionTheme = useSectionTheme("statements");
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [trends, setTrends] = useState({});
  const [previewRow, setPreviewRow] = useState(null);
  const [queueKey, setQueueKey] = useState(0);

  const years = useMemo(() => {
    const y = now.getFullYear();
    return [y - 2, y - 1, y, y + 1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = () => {
    setLoading(true);
    api
      .get(`/statements/summary`, { params: { year, month } })
      .then((r) => setSummary(r.data))
      .catch((err) => toast.error(apiError(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  useEffect(() => {
    if (!summary?.rows?.length) return;
    // Fetch trend for each property in parallel (once per session per property)
    summary.rows.forEach((r) => {
      if (trends[r.property_id]) return;
      api
        .get(`/statements/trend/${r.property_id}`, { params: { months: 12 } })
        .then((res) => setTrends((t) => ({ ...t, [r.property_id]: res.data })))
        .catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const download = async (row) => {
    try {
      const resp = await api.get(`/statements/${row.property_id}.pdf`, {
        params: { year, month },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `newport-stays-${row.property_name.replace(/[^a-z0-9]+/gi, "-")}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${row.property_name} downloaded`);
    } catch (err) {
      toast.error(apiError(err, "Download failed"));
    }
  };

  const rows = summary?.rows || [];
  const totals = summary?.totals || { revenue: 0, nights: 0, bookings: 0 };
  const topRevenue = Math.max(1, ...rows.map((r) => r.revenue));

  return (
    <Layout
      title="Owner statements"
      subtitle="One monthly revenue PDF per property. Great to forward to co-hosts, accountants or the head owner."
    >
      <div data-testid={STATEMENTS.root} className="space-y-6">
        <StatementQueueCard refreshKey={queueKey} />
        <LandlordImportCard onImported={() => setQueueKey((k) => k + 1)} />
        <section className={`ns-section ns-section-${sectionTheme}`} data-section-theme={sectionTheme}>
          <div className="flex items-start gap-3 mb-5">
            <div className={`ns-rail ns-rail-${sectionTheme} self-stretch min-h-[3rem]`} />
            <div className="flex-1 min-w-0">
              <div className="ns-label text-teal-800">Statements</div>
              <div className="font-serif text-lg text-slate-900 mt-0.5">Monthly revenue, per unit</div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-4">
          <div className="min-w-[180px]">
            <Label className="ns-label">Month</Label>
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger data-testid={STATEMENTS.monthSelect} className="mt-2 h-11 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <Label className="ns-label">Year</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger data-testid={STATEMENTS.yearSelect} className="mt-2 h-11 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2 text-teal-700">
              <TrendingUp className="w-4 h-4" />
              <div>
                <div className="ns-label">Total revenue</div>
                <div className="font-serif text-2xl text-slate-900">{gbp(totals.revenue)}</div>
              </div>
            </div>
            <div className="hidden sm:block ns-hairline h-10 w-px" style={{ width: 1 }} />
            <div className="text-slate-600">
              <div className="ns-label">Nights</div>
              <div className="font-serif text-2xl text-slate-900">{totals.nights}</div>
            </div>
            <div className="text-slate-600">
              <div className="ns-label">Bookings</div>
              <div className="font-serif text-2xl text-slate-900">{totals.bookings}</div>
            </div>
          </div>
          </div>
        </section>

        <div data-testid={STATEMENTS.table} className="ns-card overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-slate-100">
                <th className="ns-label px-6 py-4">Property</th>
                <th className="ns-label px-6 py-4">12-mo trend</th>
                <th className="ns-label px-6 py-4">Bookings</th>
                <th className="ns-label px-6 py-4">Nights</th>
                <th className="ns-label px-6 py-4">Revenue</th>
                <th className="ns-label px-6 py-4 text-right">PDF</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400">Loading…</td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="px-6 py-12 text-center text-slate-400">No bookings for this month yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.property_id} className="border-b border-slate-100">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{r.property_name}</div>
                    <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[240px]">
                      <div
                        className="h-full bg-gradient-to-r from-teal-500 to-teal-700"
                        style={{ width: `${(r.revenue / topRevenue) * 100}%` }}
                      />
                    </div>
                  </td>
                  <td className="px-6 py-4" style={{ minWidth: 160 }}>
                    <div style={{ width: 140, height: 44 }} data-testid={`statements-trend-${r.property_id}`}>
                      {trends[r.property_id]?.length > 0 ? (
                        <ResponsiveContainer width={140} height={44}>
                          <LineChart data={trends[r.property_id]}>
                            <Tooltip
                              cursor={{ stroke: "#0d7a75", strokeWidth: 1, strokeDasharray: "3 3" }}
                              content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const p = payload[0].payload;
                                return (
                                  <div className="bg-white shadow-md border border-slate-100 rounded-lg px-2.5 py-1.5 text-xs">
                                    <div className="font-medium">{p.month}</div>
                                    <div className="text-teal-700">{gbp(p.revenue)}</div>
                                    <div className="text-slate-400">{p.nights} nights · {p.bookings} bookings</div>
                                  </div>
                                );
                              }}
                            />
                            <Line
                              type="monotone"
                              dataKey="revenue"
                              stroke="#0d7a75"
                              strokeWidth={2}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      ) : (
                        <div className="w-full h-full rounded bg-slate-50 animate-pulse" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-700">{r.bookings}</td>
                  <td className="px-6 py-4 text-slate-700">{r.nights}</td>
                  <td className="px-6 py-4 font-serif text-lg text-slate-900">{gbp(r.revenue)}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button
                        data-testid={`statements-preview-${r.property_id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => setPreviewRow(r)}
                        className="rounded-full"
                        disabled={r.bookings === 0}
                        title="Preview the PDF inline"
                      >
                        <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview
                      </Button>
                      <Button
                        data-testid={STATEMENTS.downloadBtn(r.property_id)}
                        variant="outline"
                        size="sm"
                        onClick={() => download(r)}
                        className="rounded-full"
                        disabled={r.bookings === 0}
                      >
                        <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ns-card p-5 text-sm text-slate-500 flex items-start gap-3">
          <FileText className="w-4 h-4 text-teal-700 mt-0.5" />
          <div>
            Revenue is drawn from each booking&#39;s <code>total_price</code> — synced automatically from Hostaway.
            Manual bookings without a price won&#39;t contribute a figure until you edit them in.
          </div>
        </div>
      </div>
      <PdfPreviewModal
        open={!!previewRow}
        onClose={() => setPreviewRow(null)}
        endpoint={previewRow ? `/statements/${previewRow.property_id}.pdf` : null}
        params={{ year, month }}
        filename={previewRow
          ? `newport-stays-${(previewRow.property_name || "").replace(/[^a-z0-9]+/gi, "-")}-${year}-${String(month).padStart(2, "0")}.pdf`
          : "statement.pdf"}
        title={previewRow ? `${previewRow.property_name} · ${MONTHS[month - 1]} ${year}` : "Owner statement"}
      />
    </Layout>
  );
}
